import { getCollection, getInventoryConfig } from "@/api/inventory/getConfig";
import { useUploadMetadataJsonMutation } from "@/api/inventory/metadata";
import { useUpdatePricesMutation } from "@/api/inventory/price";
import { getMetadataOptions } from "@/api/nfts/getMetadata";
import { getNFTBalanceOptions } from "@/api/nfts/getNFTBalance";
import { TransferButton } from "@/components/TransferButton";
import { BurnButton } from "@/components/burn/BurnButton";
import { ListToMarketplaceButton } from "@/components/listing/ListToMarketplaceButton";
import { usePersistentForm } from "@/hooks/usePersistForm";
import { OptimizedImage } from "@/shared/OptimizedImage";
import { ErrorBoundary } from "@/shared/suspense/ErrorBoundary";
import { LoadingFallback } from "@/shared/suspense/LoadingFallback";
import {
	ActionIcon,
	Badge,
	Button,
	Card,
	Grid,
	Group,
	JsonInput,
	NumberInput,
	Paper,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { useAppKitAccount } from "@reown/appkit/react";
import { IconArrowLeft, IconCheck, IconEdit, IconX } from "@tabler/icons-react";
import { useForm } from "@tanstack/react-form";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import React from "react";
import { Suspense, useState } from "react";
import type { Address } from "viem";

export const Route = createFileRoute("/_auth/$collection/nfts_/$tokenId")({
	loader: async ({ context, params: { collection, tokenId } }) => {
		const collectionObj = await getCollection({
			queryClient: context.queryClient,
			collectionAddress: collection,
		});

		await Promise.all([
			context.queryClient.ensureQueryData(
				getMetadataOptions(collection, tokenId),
			),
		]);

		return collectionObj;
	},
	errorComponent: ({ error }) => <ErrorBoundary error={error} />,
	component: React.memo(NFTDetailComponent),
});

function NFTDetailComponent() {
	const { collection, tokenId } = Route.useParams();
	const { type } = Route.useLoaderData();
	const router = useRouter();

	const { address } = useAppKitAccount();

	const { data: metadataFromS3 } = useSuspenseQuery(
		getMetadataOptions(collection, tokenId),
	);

	const walletBalance = useQuery(
		getNFTBalanceOptions(
			type,
			collection as Address,
			Number(tokenId),
			address as Address,
		),
	);

	return (
		<Stack gap="lg">
			<Group justify="space-between">
				<Group>
					<ActionIcon variant="subtle" onClick={() => router.history.back()}>
						<IconArrowLeft />
					</ActionIcon>
					<Title order={2}>{metadataFromS3?.name}</Title>
				</Group>
				<Group>
					{!!walletBalance.data && (
						<ListToMarketplaceButton
							type={type}
							name={metadataFromS3?.name}
							collection={collection}
							tokenId={Number(tokenId)}
							size="xs"
						/>
					)}
					<TransferButton
						type={type}
						name={metadataFromS3?.name}
						collection={collection}
						tokenId={Number(tokenId)}
						size="xs"
					/>
					<BurnButton
						type={type}
						name={metadataFromS3?.name}
						collection={collection}
						tokenId={Number(tokenId)}
						size="xs"
					/>
				</Group>
			</Group>

			<Grid columns={26}>
				<Grid.Col span={{ base: 26, md: 14 }}>
					<Stack>
						<Card
							shadow="sm"
							padding="lg"
							radius="md"
							withBorder
							style={{
								viewTransitionName: `nft-image-${tokenId}`,
							}}
						>
							<Card.Section>
								<OptimizedImage
									src={metadataFromS3?.image}
									style={{ objectFit: "contain", aspectRatio: "1" }}
									alt={metadataFromS3?.name || `NFT #${tokenId}`}
								/>
							</Card.Section>
						</Card>

						<Paper shadow="sm" p="md" radius="md" withBorder>
							<Stack gap="xs">
								<Group wrap="wrap" gap="xs">
									<Badge color="blue" variant="light" size="xl">
										Token ID: {tokenId}
									</Badge>
								</Group>
								<Suspense
									fallback={<LoadingFallback size="sm" fullHeight={false} />}
								>
									<NFTDisplayWeight />
								</Suspense>
								<Suspense
									fallback={<LoadingFallback size="sm" fullHeight={false} />}
								>
									<NFTPrice />
								</Suspense>
								<Text fw={500}>Attributes:</Text>
								<Group gap="xs" wrap="wrap">
									{metadataFromS3?.attributes?.map(
										(
											attr: { trait_type: string; value: string },
											index: number,
										) => (
											<Badge
												key={`${attr?.trait_type}-${index}`}
												color="gray"
												variant="outline"
												size="lg"
											>
												{attr?.trait_type}: {attr?.value}
											</Badge>
										),
									)}
								</Group>
							</Stack>
						</Paper>
					</Stack>
				</Grid.Col>

				<Grid.Col span={{ base: 26, md: 12 }}>
					<Suspense fallback={<LoadingFallback size="sm" fullHeight={false} />}>
						<NFTMetadata />
					</Suspense>
				</Grid.Col>
			</Grid>
		</Stack>
	);
}

function NFTMetadata() {
	const { collection, tokenId } = Route.useParams();
	const [isEditing, setIsEditing] = useState(false);
	const updateMetadataJson = useUploadMetadataJsonMutation();

	const { data: metadataFromS3 } = useSuspenseQuery(
		getMetadataOptions(collection, tokenId),
	);

	const { price, sellbackPriceInCredits, sellbackPriceInUsd, ...onlyMetadata } =
		metadataFromS3;

	const form = useForm({
		defaultValues: {
			metadata: JSON.stringify(onlyMetadata, null, 2),
		},
		onSubmit: ({ value }) => {
			updateMetadataJson.mutate(
				{
					collectionAddress: collection,
					metadata: [
						{
							...JSON.parse(value.metadata),
							tokenId: Number(tokenId),
						},
					],
				},
				{
					onSuccess: () => {
						setIsEditing(false);
						form.reset();
					},
				},
			);
		},
	});

	return (
		<Paper shadow="sm" p="md" radius="md" withBorder style={{ flex: 1 }}>
			<Stack gap="md">
				<Group justify="space-between">
					<Text fw={500}>Metadata</Text>
					{isEditing ? (
						<Group gap="xs">
							<Button
								variant="subtle"
								color="gray"
								onClick={() => {
									const {
										price,
										sellbackPriceInCredits,
										sellbackPriceInUsd,
										...onlyMetadata
									} = metadataFromS3;

									form.setFieldValue(
										"metadata",
										JSON.stringify(onlyMetadata, null, 2),
									);
									setIsEditing(false);
								}}
								loaderProps={{ type: "dots" }}
							>
								Reset
							</Button>
							<Button
								onClick={() => form.handleSubmit()}
								loading={updateMetadataJson.isPending}
								loaderProps={{ type: "dots" }}
							>
								Save Changes
							</Button>
						</Group>
					) : (
						<ActionIcon
							variant="subtle"
							onClick={() => setIsEditing(!isEditing)}
						>
							<IconEdit />
						</ActionIcon>
					)}
				</Group>

				<form.Subscribe selector={(state) => state.values.metadata}>
					{(metadata) => (
						<form.Field
							name="metadata"
							validators={{
								onChange: ({ value }) => {
									try {
										JSON.parse(value);
										return undefined;
									} catch {
										return "Invalid JSON format";
									}
								},
							}}
						>
							{(field) => (
								<JsonInput
									value={metadata}
									onChange={field.handleChange}
									onBlur={field.handleBlur}
									disabled={!isEditing}
									styles={{
										input: {
											height: "73dvh",
											whiteSpace: "pre",
											overflow: "scroll",
											scrollbarWidth: "thin",
										},
									}}
									formatOnBlur
									error={field.state.meta.errors[0]}
								/>
							)}
						</form.Field>
					)}
				</form.Subscribe>
			</Stack>
		</Paper>
	);
}

function NFTDisplayWeight() {
	const { collection, tokenId } = Route.useParams();
	const [isEditing, setIsEditing] = useState(false);
	const updateMetadataJson = useUploadMetadataJsonMutation();
	const inputRef = React.useRef<HTMLInputElement>(null);

	React.useEffect(() => {
		if (isEditing && inputRef.current) {
			inputRef.current.focus();
			// Set cursor position to the end
			const length = inputRef.current.value.length;
			inputRef.current.setSelectionRange(length, length);
		}
	}, [isEditing]);

	const { data: metadataFromS3 } = useSuspenseQuery(
		getMetadataOptions(collection, tokenId),
	);

	const { price, sellbackPriceInCredits, sellbackPriceInUsd, ...onlyMetadata } =
		metadataFromS3;

	const form = useForm({
		defaultValues: {
			displayWeight: metadataFromS3?.displayWeight ?? 0,
		},
		onSubmit: ({ value }) => {
			updateMetadataJson.mutate(
				{
					collectionAddress: collection,
					metadata: [
						{
							...onlyMetadata,
							displayWeight: value.displayWeight,
							tokenId: Number(tokenId),
						},
					],
				},
				{
					onSuccess: () => {
						setIsEditing(false);
						form.reset();
					},
				},
			);
		},
	});

	return (
		<Group gap="xs">
			<Badge color="pink" variant="light" size="xl">
				<form.Subscribe selector={(state) => state.values.displayWeight}>
					{(displayWeight) => (
						<Group gap={4}>
							Display Weight:
							<form.Field
								name="displayWeight"
								validators={{
									onChange: ({ value }) =>
										value < 0
											? "Display weight must be greater than or equal to 0"
											: undefined,
								}}
							>
								{(field) => (
									<NumberInput
										ref={inputRef}
										value={displayWeight}
										onChange={(val) => field.handleChange(val as number)}
										onBlur={field.handleBlur}
										placeholder=""
										min={0}
										allowDecimal={false}
										size="md"
										variant="unstyled"
										disabled={!isEditing}
										hideControls
										styles={{
											input: {
												background: "none",
												color: "#e64981",
												width: `${String(displayWeight).length + 1}ch`,
												textAlign: "center",
												cursor: isEditing ? "text" : "default",
												opacity: 1,
											},
										}}
										error={field.state.meta.errors[0]}
									/>
								)}
							</form.Field>
						</Group>
					)}
				</form.Subscribe>
			</Badge>
			{isEditing ? (
				<>
					<ActionIcon
						variant="light"
						color="red"
						size="md"
						onClick={() => {
							setIsEditing(false);
							form.reset();
						}}
					>
						<IconX size={16} />
					</ActionIcon>

					<ActionIcon
						variant="light"
						color="green"
						size="md"
						onClick={() => form.handleSubmit()}
						loading={updateMetadataJson.isPending}
						disabled={!form.state.canSubmit}
					>
						<IconCheck size={16} />
					</ActionIcon>
				</>
			) : (
				<ActionIcon
					variant="subtle"
					size="sm"
					onClick={() => setIsEditing(true)}
				>
					<IconEdit />
				</ActionIcon>
			)}
		</Group>
	);
}

function NFTPrice() {
	const { collection, tokenId } = Route.useParams();
	const [isPriceEditing, setIsPriceEditing] = useState(false);
	const updatePrices = useUpdatePricesMutation();

	const { data: nft } = useSuspenseQuery(
		getMetadataOptions(collection, tokenId),
	);

	const defaultValues = {
		price: nft?.price ?? 0,
		sellbackPriceInUsd: nft?.sellbackPriceInUsd ?? 0,
		sellbackPriceInCredits: nft?.sellbackPriceInCredits ?? 0,
	};

	const form = usePersistentForm(
		`form${window.location.pathname.replace(/\//g, "-")}`,
		{
			defaultValues,
			onSubmit: ({ value }) => {
				updatePrices.mutate(
					{
						collectionAddress: collection,
						prices: [
							{
								tokenId: Number(tokenId),
								priceInUsd: value.price,
								sellbackPriceInCredits: value.sellbackPriceInCredits,
								sellbackPriceInUsd: value.sellbackPriceInUsd,
							},
						],
					},
					{
						onSuccess: () => {
							setIsPriceEditing(false);
						},
					},
				);
			},
		},
	);

	if (isPriceEditing) {
		return (
			<Group align="end" gap="xs">
				<form.Subscribe selector={(state) => state.values}>
					{(values) => (
						<>
							<form.Field
								name="price"
								validators={{
									onChange: ({ value }) =>
										value < 0
											? "Price must be greater than or equal to 0"
											: undefined,
								}}
							>
								{(field) => (
									<NumberInput
										flex={1}
										label="Price"
										value={values.price}
										onChange={(val) => field.handleChange(val as number)}
										onBlur={field.handleBlur}
										placeholder="Enter price"
										min={0}
										allowDecimal
										decimalScale={2}
										fixedDecimalScale
										prefix="$"
										size="md"
										error={field.state.meta.errors[0]}
									/>
								)}
							</form.Field>

							<form.Field
								name="sellbackPriceInUsd"
								validators={{
									onChange: ({ value }) =>
										value < 0
											? "Sellback price must be greater than or equal to 0"
											: undefined,
								}}
							>
								{(field) => (
									<NumberInput
										flex={1}
										label="Sellback USD"
										value={values.sellbackPriceInUsd}
										onChange={(val) => field.handleChange(val as number)}
										onBlur={field.handleBlur}
										placeholder="Enter sellback price"
										min={0}
										allowDecimal
										decimalScale={2}
										fixedDecimalScale
										prefix="$"
										size="md"
										error={field.state.meta.errors[0]}
									/>
								)}
							</form.Field>
							<form.Field
								name="sellbackPriceInCredits"
								validators={{
									onChange: ({ value }) =>
										value < 0
											? "Credits must be greater than or equal to 0"
											: undefined,
								}}
							>
								{(field) => (
									<NumberInput
										flex={1}
										label="Sellback Credits"
										value={values.sellbackPriceInCredits}
										onChange={(val) => field.handleChange(val as number)}
										onBlur={field.handleBlur}
										placeholder="Enter credits"
										min={0}
										allowDecimal
										decimalScale={2}
										fixedDecimalScale
										prefix="$"
										size="md"
										error={field.state.meta.errors[0]}
									/>
								)}
							</form.Field>
						</>
					)}
				</form.Subscribe>

				<ActionIcon
					variant="light"
					color="red"
					size="lg"
					onClick={() => {
						setIsPriceEditing(false);
						if (nft) {
							form.setFieldValue("price", nft.price ?? 0);
							form.setFieldValue(
								"sellbackPriceInUsd",
								nft.sellbackPriceInUsd ?? 0,
							);
							form.setFieldValue(
								"sellbackPriceInCredits",
								nft.sellbackPriceInCredits ?? 0,
							);
						}
					}}
				>
					<IconX size={16} />
				</ActionIcon>

				<ActionIcon
					variant="light"
					color="green"
					size="lg"
					onClick={() => form.handleSubmit()}
					loading={updatePrices.isPending}
					disabled={!form.state.canSubmit}
				>
					<IconCheck size={16} />
				</ActionIcon>
			</Group>
		);
	}

	return (
		<Group gap="xs">
			<Badge color="teal" variant="light" size="xl">
				Price: ${typeof nft?.price === "number" ? nft.price.toFixed(2) : "0.00"}
			</Badge>
			<Badge color="lime" variant="light" size="xl">
				Sellback USD: $
				{typeof nft?.sellbackPriceInUsd === "number"
					? nft.sellbackPriceInUsd.toFixed(2)
					: "0.00"}
			</Badge>
			<Badge color="green" variant="light" size="xl">
				Sellback Credits: $
				{typeof nft?.sellbackPriceInCredits === "number"
					? nft.sellbackPriceInCredits.toFixed(2)
					: "0.00"}
			</Badge>
			<ActionIcon
				variant="subtle"
				size="sm"
				onClick={() => setIsPriceEditing(true)}
			>
				<IconEdit />
			</ActionIcon>
		</Group>
	);
}
